import { supabase, supabaseAdmin } from '../lib/supabase';
import { getEdgeFunctionErrorMessage } from '../lib/edgeFunctionErrors';

export const userRoleService = {
  // ============================================
  // USER ROLES MANAGEMENT
  // ============================================

  // Get all user roles
  getAllRoles: async () => {
    try {
      const { data, error } = await supabase?.from('user_roles')?.select('*')?.order('role_name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(role => ({
        id: role?.id,
        roleType: role?.role_type,
        roleName: role?.role_name,
        description: role?.description,
        canBeClient: role?.can_be_client,
        canBeSupplier: role?.can_be_supplier,
        isActive: role?.is_active !== false,
        createdAt: role?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user roles:', error);
      throw error;
    }
  },

  createRole: async (payload = {}) => {
    try {
      const { data, error } = await supabase
        ?.from('user_roles')
        ?.insert({
          role_type: payload?.roleType,
          role_name: payload?.roleName,
          description: payload?.description ?? null,
          can_be_client: !!payload?.canBeClient,
          can_be_supplier: !!payload?.canBeSupplier,
          is_active: payload?.isActive !== false,
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        roleType: data?.role_type,
        roleName: data?.role_name,
        description: data?.description,
        canBeClient: data?.can_be_client,
        canBeSupplier: data?.can_be_supplier,
        isActive: data?.is_active !== false,
        createdAt: data?.created_at,
      };
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  updateRole: async (roleId, payload = {}) => {
    try {
      const row = {};
      if (payload?.roleName !== undefined) row.role_name = payload.roleName;
      if (payload?.description !== undefined) row.description = payload.description;
      if (payload?.canBeClient !== undefined) row.can_be_client = !!payload.canBeClient;
      if (payload?.canBeSupplier !== undefined) row.can_be_supplier = !!payload.canBeSupplier;
      if (payload?.isActive !== undefined) row.is_active = !!payload.isActive;

      const { data, error } = await supabase
        ?.from('user_roles')
        ?.update(row)
        ?.eq('id', roleId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        roleType: data?.role_type,
        roleName: data?.role_name,
        description: data?.description,
        canBeClient: data?.can_be_client,
        canBeSupplier: data?.can_be_supplier,
        isActive: data?.is_active !== false,
        createdAt: data?.created_at,
      };
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  },

  deleteRole: async (roleId) => {
    try {
      const { error } = await supabase?.from('user_roles')?.delete()?.eq('id', roleId);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  },

  countRoleAssignments: async (roleId) => {
    try {
      const { count, error } = await supabase
        ?.from('user_role_assignments')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('role_id', roleId);

      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error('Error counting role assignments:', error);
      throw error;
    }
  },

   getAllCountries: async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('id, country_name')
        .eq('is_active', true)
        .order('country_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      throw error;
    }
  },

  // ============================================
  // USER ROLE ASSIGNMENTS
  // ============================================

  // Get role assignments for a user
  getUserRoleAssignments: async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_role_assignments')?.select('*, role:user_roles(*), country:countries(*)')?.eq('user_id', userId);
      
      if (error) throw error;
      
      return data?.map(assignment => ({
        id: assignment?.id,
        userId: assignment?.user_id,
        roleId: assignment?.role_id,
        role: assignment?.role,
        countryId: assignment?.country_id,
        country: assignment?.country,
        regionAccess: assignment?.region_access,
        isActive: assignment?.is_active,
        assignedAt: assignment?.assigned_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user role assignments:', error);
      throw error;
    }
  },

  // Get all role assignments with user details
  getAllRoleAssignments: async (filters = {}) => {
    try {
      let query = supabase?.from('user_role_assignments')?.select('*, role:user_roles(*), country:countries(*)');
      
      if (filters?.roleId) {
        query = query?.eq('role_id', filters?.roleId);
      }
      if (filters?.countryId) {
        query = query?.eq('country_id', filters?.countryId);
      }
      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }
      
      const { data, error } = await query?.order('assigned_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch user profiles separately
      const userIds = [...new Set(data?.map(a => a?.user_id).filter(Boolean))];
      let userProfiles = [];
      
      if (userIds?.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.in('id', userIds);
        
        if (!profilesError) {
          userProfiles = profiles || [];
        }
      }
      
      // Map user profiles to assignments
      return data?.map(assignment => {
        const userProfile = userProfiles?.find(u => u?.id === assignment?.user_id);
        return {
          id: assignment?.id,
          userId: assignment?.user_id,
          user: userProfile || null,
          roleId: assignment?.role_id,
          role: assignment?.role,
          countryId: assignment?.country_id,
          country: assignment?.country,
          regionAccess: assignment?.region_access,
          isActive: assignment?.is_active,
          assignedAt: assignment?.assigned_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching all role assignments:', error);
      throw error;
    }
  },

  // Assign role to user
  assignRole: async (assignmentData) => {
    try {
      const { data, error } = await supabase?.from('user_role_assignments')?.insert({
        user_id: assignmentData?.userId,
        role_id: assignmentData?.roleId,
        country_id: assignmentData?.countryId || null,
        region_access: assignmentData?.regionAccess || [],
        is_active: assignmentData?.isActive !== undefined ? assignmentData?.isActive : true
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        roleId: data?.role_id,
        countryId: data?.country_id,
        regionAccess: data?.region_access,
        isActive: data?.is_active,
        assignedAt: data?.assigned_at
      };
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  },

  // Update role assignment
  updateRoleAssignment: async (id, assignmentData) => {
    try {
      const updateData = {};
      if (assignmentData?.countryId !== undefined) updateData.country_id = assignmentData?.countryId;
      if (assignmentData?.regionAccess !== undefined) updateData.region_access = assignmentData?.regionAccess;
      if (assignmentData?.isActive !== undefined) updateData.is_active = assignmentData?.isActive;
      
      const { data, error } = await supabase?.from('user_role_assignments')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        roleId: data?.role_id,
        countryId: data?.country_id,
        regionAccess: data?.region_access,
        isActive: data?.is_active,
        assignedAt: data?.assigned_at
      };
    } catch (error) {
      console.error('Error updating role assignment:', error);
      throw error;
    }
  },

  // Remove role assignment
  removeRoleAssignment: async (id) => {
    try {
      const { error } = await supabase?.from('user_role_assignments')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error removing role assignment:', error);
      throw error;
    }
  },

  // ============================================
  // PERMISSIONS MANAGEMENT
  // ============================================

  // Get all permissions
  getAllPermissions: async () => {
    try {
      const { data, error } = await supabase?.from('permissions')?.select('*')?.order('module', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(permission => ({
        id: permission?.id,
        permissionCode: permission?.permission_code,
        permissionName: permission?.permission_name,
        description: permission?.description,
        module: permission?.module,
        displayGroup: permission?.display_group,
        isConfigurable: permission?.is_configurable,
        sortOrder: permission?.sort_order,
        createdAt: permission?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  },

  // Get permissions for a role
  getRolePermissions: async (roleId) => {
    try {
      const { data, error } = await supabase?.from('role_permissions')?.select('*, permission:permissions(*)')?.eq('role_id', roleId);
      
      if (error) throw error;
      
      return data?.map(rp => ({
        id: rp?.id,
        roleId: rp?.role_id,
        permissionId: rp?.permission_id,
        permission: rp?.permission,
        createdAt: rp?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      throw error;
    }
  },

  // Assign permission to role
  assignPermission: async (roleId, permissionId) => {
    try {
      const { data, error } = await supabase
        ?.from('role_permissions')
        ?.insert({ role_id: roleId, permission_id: permissionId })
        ?.select('id, role_id, permission_id, created_at')
        ?.maybeSingle();

      if (error) {
        if (error.code === '23505') {
          const { data: existing, error: readErr } = await supabase
            ?.from('role_permissions')
            ?.select('id, role_id, permission_id, created_at')
            ?.eq('role_id', roleId)
            ?.eq('permission_id', permissionId)
            ?.maybeSingle();
          if (readErr) throw readErr;
          if (existing?.id) {
            return {
              id: existing.id,
              roleId: existing.role_id,
              permissionId: existing.permission_id,
              createdAt: existing.created_at,
            };
          }
        }
        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error(
            'Droits insuffisants : seul un compte avec le rôle Administrateur peut modifier la matrice.',
          );
        }
        throw error;
      }

      if (!data?.id) {
        throw new Error(
          'Permission non enregistrée. Vérifiez votre rôle Administrateur (affectation active) ou appliquez la migration RLS.',
        );
      }

      return {
        id: data?.id,
        roleId: data?.role_id,
        permissionId: data?.permission_id,
        createdAt: data?.created_at,
      };
    } catch (error) {
      console.error('Error assigning permission:', error);
      throw error;
    }
  },

  // Remove permission from role
  removePermission: async (roleId, permissionId) => {
    try {
      const { error, count } = await supabase
        ?.from('role_permissions')
        ?.delete({ count: 'exact' })
        ?.eq('role_id', roleId)
        ?.eq('permission_id', permissionId);

      if (error) {
        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error(
            'Droits insuffisants : seul un compte avec le rôle Administrateur peut modifier la matrice.',
          );
        }
        throw error;
      }

      if (count === 0) {
        throw new Error(
          'Aucune permission retirée (ligne absente ou droits insuffisants).',
        );
      }

      return { success: true };
    } catch (error) {
      console.error('Error removing permission:', error);
      throw error;
    }
  },

  // Assign permission to role (alias for consistency)
  assignPermissionToRole: async (roleId, permissionId) => {
    return userRoleService?.assignPermission(roleId, permissionId);
  },

  // Remove permission from role (alias for consistency)
  removePermissionFromRole: async (roleId, permissionId) => {
    return userRoleService?.removePermission(roleId, permissionId);
  },

  /**
   * Ajoute les permission_code du profil recommandé (ne retire pas les droits existants).
   * @param {string} roleId
   * @param {string} roleType
   * @param {string[]|null} presetCodes — null = administrateur (toutes les permissions)
   */
  applyRoleCapabilityPreset: async (roleId, roleType, presetCodes) => {
    const allPerms = await userRoleService.getAllPermissions();
    const codeToId = Object.fromEntries(
      (allPerms || []).map((p) => [p.permissionCode, p.id]).filter(([c]) => c),
    );

    let codes = presetCodes;
    if (roleType === 'administrator' || codes === null) {
      codes = (allPerms || []).map((p) => p.permissionCode).filter(Boolean);
    }
    if (!codes?.length) {
      return { added: 0, skipped: 0 };
    }

    const current = await userRoleService.getRolePermissions(roleId);
    const currentIds = new Set((current || []).map((rp) => rp.permissionId));

    let added = 0;
    let skipped = 0;
    for (const code of [...new Set(codes)]) {
      const permId = codeToId[code];
      if (!permId) {
        skipped += 1;
        continue;
      }
      if (currentIds.has(permId)) {
        skipped += 1;
        continue;
      }
      await userRoleService.assignPermission(roleId, permId);
      added += 1;
    }
    return { added, skipped };
  },

  // ============================================
  // USER PROFILES MANAGEMENT (CRUD)
  // ============================================

  // Get all user profiles with filters
  getAllUserProfiles: async (filters = {}) => {
    try {
      let query = supabase?.from('user_profiles')?.select('*');
      
      if (filters?.status) {
        // Map status filter to is_active boolean
        const isActive = filters?.status === 'active';
        query = query?.eq('is_active', isActive);
      }
      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(profile => ({
        id: profile?.id,
        fullName: profile?.full_name,
        email: profile?.email,
        phoneNumber: profile?.phone,
        status: profile?.is_active ? 'active' : 'inactive',
        lastLoginAt: profile?.last_login_at,
        createdAt: profile?.created_at,
        updatedAt: profile?.updated_at
      })) || [];
  
    } catch (error) {
      console.error('Error fetching user profiles:', error);
      throw error;
    }
  },

  // Get single user profile by ID
  getUserProfile: async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        phoneNumber: data?.phone,
        status: data?.is_active ? 'active' : 'inactive',
        lastLoginAt: data?.last_login_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Create new user with auth and profile
  createUser: async (userData) => {

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(userData?.email)) {
        throw new Error('Invalid email format. Please use a valid email address.');
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('Session expirée : reconnectez-vous en tant qu’administrateur.');
      }

      const { data: payload, error: invokeError } = await supabase.functions.invoke('create-user', {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: {
          email: userData?.email,
          password: userData?.password,
          full_name: userData?.fullName,
          country_id: userData?.countryId || null,
          phone: userData?.phone || null,
        },
      });

      const serverMessage = await getEdgeFunctionErrorMessage(invokeError, payload);
      if (invokeError || payload?.error) {
        const msg =
          serverMessage ||
          'Échec de création (vérifiez que la fonction create-user est déployée : supabase functions deploy create-user).';
        if (msg.includes('invalid') || msg.includes('Email') || msg.includes('already')) {
          throw new Error(
            `E-mail : ${msg}. Utilisez une autre adresse si le compte existe déjà.`,
          );
        }
        throw new Error(msg);
      }

      const profile = payload?.profile;
      const userId = profile?.id ?? payload?.user?.id;

      if (!userId) {
        throw new Error('Création utilisateur : identifiant manquant dans la réponse serveur.');
      }

      return {
        id: userId,
        fullName: profile?.full_name ?? userData?.fullName,
        email: profile?.email ?? userData?.email,
        phoneNumber: profile?.phone ?? userData?.phone,
        status: profile?.is_active !== false ? 'active' : 'inactive',
        createdAt: profile?.created_at,
      };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (userId, profileData) => {
    try {
      const updateData = {};
      if (profileData?.fullName !== undefined) updateData.full_name = profileData?.fullName;
      if (profileData?.email !== undefined) updateData.email = profileData?.email;
      if (profileData?.phoneNumber !== undefined) updateData.phone = profileData?.phoneNumber;
      if (profileData?.status !== undefined) {
        // Map status to is_active boolean
        updateData.is_active = profileData?.status === 'active';
      }
      
      const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        phoneNumber: data?.phone,
        status: data?.is_active ? 'active' : 'inactive',
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Delete user (soft delete by setting is_active to false)
  deleteUser: async (userId) => {
    try {
      // Soft delete: set is_active to false
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', userId);
      if (error) throw error;
      
      // Also deactivate all role assignments
      await supabase?.from('user_role_assignments')?.update({ is_active: false })?.eq('user_id', userId);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Hard delete user (permanent deletion)
  permanentDeleteUser: async (userId) => {
    try {
      // Delete role assignments first
      await supabase?.from('user_role_assignments')?.delete()?.eq('user_id', userId);
      
      // Delete profile
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);
      if (error) throw error;
      
      // Delete auth user
      const { error: authError } = await supabase?.auth?.admin?.deleteUser(userId);
      if (authError) console.error('Error deleting auth user:', authError);
      
      return { success: true };
    } catch (error) {
      console.error('Error permanently deleting user:', error);
      throw error;
    }
  },

  // Bulk update user status
  bulkUpdateUserStatus: async (userIds, status) => {
    try {
      // Map status to is_active boolean
      const isActive = status === 'active';
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: isActive })?.in('id', userIds);
      if (error) throw error;
      return { success: true, count: userIds?.length };
    } catch (error) {
      console.error('Error bulk updating user status:', error);
      throw error;
    }
  },

  // Bulk delete users
  bulkDeleteUsers: async (userIds) => {
    try {
      // Soft delete: set is_active to false
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.in('id', userIds);
      if (error) throw error;
      
      // Deactivate all role assignments
      await supabase?.from('user_role_assignments')?.update({ is_active: false })?.in('user_id', userIds);
      
      return { success: true, count: userIds?.length };
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      throw error;
    }
  },

  // Bulk assign role to multiple users
  bulkAssignRole: async (userIds, roleId, countryId = null) => {
    try {
      const assignments = userIds?.map(userId => ({
        user_id: userId,
        role_id: roleId,
        country_id: countryId,
        is_active: true
      }));
      
      const { data, error } = await supabase?.from('user_role_assignments')?.insert(assignments)?.select();
      if (error) throw error;
      
      return { success: true, count: data?.length };
    } catch (error) {
      console.error('Error bulk assigning role:', error);
      throw error;
    }
  },

  // Reset user password (admin — Edge Function + service role côté serveur)
  resetUserPassword: async (userId, newPassword) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('Session expirée : reconnectez-vous en tant qu’administrateur.');
      }

      const { data: payload, error: invokeError } = await supabase.functions.invoke(
        'reset-user-password',
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          body: { user_id: userId, password: newPassword },
        },
      );

      const serverMessage = await getEdgeFunctionErrorMessage(invokeError, payload);
      if (invokeError || payload?.error) {
        const msg =
          serverMessage ||
          'Échec de réinitialisation (vérifiez que la fonction reset-user-password est déployée).';
        throw new Error(msg);
      }

      return { success: true };
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  },

  // Get user with all roles and permissions
  getUserWithRolesAndPermissions: async (userId) => {
    try {
      // Get user profile
      const profile = await userRoleService?.getUserProfile(userId);
      
      // Get user role assignments
      const assignments = await userRoleService?.getUserRoleAssignments(userId);
      
      // Get all permissions for user's roles
      const roleIds = assignments?.map(a => a?.roleId);
      const allPermissions = [];
      
      for (const roleId of roleIds) {
        const perms = await userRoleService?.getRolePermissions(roleId);
        allPermissions?.push(...perms);
      }
      
      // Remove duplicates
      const uniquePermissions = allPermissions?.filter((perm, index, self) =>
        index === self?.findIndex(p => p?.permissionId === perm?.permissionId)
      );
      
      return {
        profile,
        roleAssignments: assignments,
        permissions: uniquePermissions
      };
    } catch (error) {
      console.error('Error fetching user with roles and permissions:', error);
      throw error;
    }
  }
};

export default userRoleService;